import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Avatar, 
  Tag, 
  Button, 
  Popconfirm,
  Empty,
  Tooltip,
  Divider,
  Progress,
} from 'antd';
import {
  PlayCircleOutlined,
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  RobotOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { NodeDetailResponse, NodeScenario, LifetreeDetailResponse } from '../../api/models/Lifetree';

const { Text } = Typography;

interface NodeScenariosTabProps {
  nodeDetail: NodeDetailResponse;
  lifetreeDetail: LifetreeDetailResponse | null;
  onShowCreateScenarioModal: () => void;
  onShowSampleScenarioModal: () => void;
  onProposeScenarios: () => Promise<void>;
  proposingScenarios: boolean;
  onEditScenario: (scenario: NodeScenario) => void;
  onViewScenarioImage: (scenario: NodeScenario) => void;
  onCreateScenarioImage: (scenario: NodeScenario) => void;
  onQuickGenScenarioImage: (scenario: NodeScenario) => void;
  onActivateScenario: (scenarioId: string) => void;
  onDeleteScenario: (scenarioId: string) => void;
  onDeployScenarios: () => Promise<void>;
  deployingScenarios: boolean;
  deploymentScenarioProgress: {
    total: number;
    current: number;
    status: string;
  } | null;
  activatingScenarioIds: Set<string>;
  quickGenScenarioIds: Set<string>;
  getCharacterNameById: (charId: string) => string;
}

const NodeScenariosTab: React.FC<NodeScenariosTabProps> = ({
  nodeDetail,
  lifetreeDetail,
  onShowCreateScenarioModal,
  onShowSampleScenarioModal,
  onProposeScenarios,
  proposingScenarios,
  onEditScenario,
  onViewScenarioImage,
  onCreateScenarioImage,
  onQuickGenScenarioImage,
  onActivateScenario,
  onDeleteScenario,
  onDeployScenarios,
  deployingScenarios,
  deploymentScenarioProgress,
  activatingScenarioIds,
  quickGenScenarioIds,
  getCharacterNameById,
}) => {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card 
        title={
          <Space>
            <PlayCircleOutlined style={{ color: '#52c41a' }} />
            <span>Danh sách Scenario</span>
          </Space>
        }
        size="small"
        extra={
          <Space>
            {nodeDetail.scenarios.some(s => s.status === 'draft') && (
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={onDeployScenarios}
                loading={deployingScenarios}
              >
                Triển khai kịch bản (AI)
              </Button>
            )}
            <Button
              type="default"
              icon={<ThunderboltOutlined style={{ color: '#faad14' }} />}
              onClick={onProposeScenarios}
              loading={proposingScenarios}
            >
              Gợi ý Scenario (AI)
            </Button>
            <Button 
              type="primary" 
              icon={<RobotOutlined />}
              onClick={onShowSampleScenarioModal}
            >
              Tạo Sample bằng AI
            </Button>
            <Button 
              icon={<PlusOutlined />}
              onClick={onShowCreateScenarioModal}
            >
              Tạo thủ công
            </Button>
          </Space>
        }
      >
        {deploymentScenarioProgress && (
          <div style={{ marginBottom: 16, padding: '12px', background: '#f0f5ff', borderRadius: '4px', border: '1px solid #adc6ff' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>{deploymentScenarioProgress.status}</Text>
                <Text type="secondary">{deploymentScenarioProgress.current}/{deploymentScenarioProgress.total}</Text>
              </div>
              <Progress 
                percent={Math.round((deploymentScenarioProgress.current / deploymentScenarioProgress.total) * 100)} 
                status="active" 
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              />
            </Space>
          </div>
        )}
        {nodeDetail.scenarios && nodeDetail.scenarios.length > 0 ? (
          <Row gutter={[16, 16]}>
            {nodeDetail.scenarios.map((scenario: NodeScenario) => (
              <Col key={scenario.id} xs={24} md={12}>
                <Card 
                  size="small" 
                  hoverable
                  style={{ height: '100%', borderLeft: `4px solid ${scenario.status === 'active' ? '#52c41a' : '#faad14'}` }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Row gutter={16}>
                    <Col span={5}>
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '2/3',
                          background: '#f5f5f5',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                        onClick={() => onViewScenarioImage(scenario)}
                      >
                        {scenario.imageUrl ? (
                          <img 
                            src={scenario.imageUrl} 
                            alt={scenario.name}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <div style={{ textAlign: 'center', color: '#bfbfbf' }}>
                            <PictureOutlined style={{ fontSize: '32px' }} />
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>Chưa có ảnh</Text>
                          </div>
                        )}
                        <Tag 
                          color={scenario.status === 'active' ? 'green' : 'orange'}
                          style={{ position: 'absolute', top: -8, right: -8, fontSize: '10px' }}
                        >
                          {scenario.status.toUpperCase()}
                        </Tag>
                      </div>
                    </Col>
                    <Col span={19}>
                      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 4 }}>{scenario.name}</Text>
                          <Space direction="vertical" size={0}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              <strong>Địa điểm:</strong> {scenario.location}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                              <strong>Thời gian:</strong> {scenario.time}
                            </Text>
                          </Space>
                          
                          {scenario.npcIds && scenario.npcIds.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>NPC tham gia:</Text>
                              <Space size={-8} wrap>
                                {scenario.npcIds.map(npcId => {
                                  const char = lifetreeDetail?.characters.find(c => c.id === npcId);
                                  return (
                                    <Tooltip title={char?.name || 'Unknown NPC'} key={npcId}>
                                      <Avatar 
                                        size="small" 
                                        src={char?.imageUrl} 
                                        icon={<UserOutlined />} 
                                        style={{ border: '1px solid #fff' }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Space>
                            </div>
                          )}
                        </div>
                        
                        <Divider style={{ margin: '12px 0' }} />
                        
                        <Space wrap>
                          <Button 
                            size="small" 
                            icon={<EditOutlined />} 
                            onClick={() => onEditScenario(scenario)}
                          >
                            Sửa
                          </Button>
                          <Button 
                            size="small" 
                            icon={<CameraOutlined />} 
                            onClick={() => onCreateScenarioImage(scenario)}
                          >
                            Gen Ảnh
                          </Button>
                          <Button 
                            size="small" 
                            type="dashed"
                            icon={<ThunderboltOutlined style={{ color: '#faad14' }} />} 
                            onClick={() => onQuickGenScenarioImage(scenario)}
                            loading={quickGenScenarioIds.has(scenario.id)}
                            disabled={scenario.status === 'active' && !!scenario.imageUrl}
                          >
                            Tạo ảnh nhanh
                          </Button>
                          {scenario.status === 'draft' && (() => {
                            const hasImage = !!scenario.imageUrl;
                            const canActivate = hasImage;
                            const tooltipContent = (
                              <div style={{ minWidth: 180 }}>
                                <div style={{ marginBottom: 6, fontWeight: 600 }}>Điều kiện kích hoạt scenario:</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {hasImage
                                    ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                    : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                  <span>Scenario có ảnh</span>
                                </div>
                              </div>
                            );
                            return (
                              <Tooltip title={tooltipContent}>
                                <span style={{ display: 'inline-block', cursor: canActivate ? 'pointer' : 'not-allowed' }}>
                                  <Button
                                    size="small"
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    loading={activatingScenarioIds.has(scenario.id)}
                                    disabled={!canActivate}
                                    onClick={() => onActivateScenario(scenario.id)}
                                    style={canActivate
                                      ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                                      : { pointerEvents: 'none' }}
                                  >
                                    Kích hoạt
                                  </Button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Popconfirm
                            title="Xóa scenario này?"
                            onConfirm={() => onDeleteScenario(scenario.id)}
                            okText="Có"
                            cancelText="Không"
                          >
                            <Button size="small" danger type="text">Xóa</Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Empty description="Chưa có scenario nào" />
        )}
      </Card>
    </Space>
  );
};

export default NodeScenariosTab;
